/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import './slotEditor.scss';
import DndSlotItemHandler from './DndSlotItemHandler';
import SlotEditorItemFlyout from '../SlotEditorItemFlyout';
import { TruncatedText, SVGIcon } from 'ca-ui-toolkit';
import menu_overflow16 from '@ba-ui-toolkit/ba-graphics/dist/icons/overflow-menu--horizontal_16.svg';

/**
 * The SlotEditorItem component is responsible for the User Interface for a single slot data item
 */
class SlotEditorItem extends Component {
	constructor(props) {
		super(props);
		this.state = {
			isFlyoutShown: false
		};
		this.sourceId = this.props.slotDataItemHandler.getDatasourceId();
	}

	componentDidMount() {
		this._initializeDndSupport();
	}


	/**
	 * A slot item consists of the name, a menu (to launch actions), an itemValues section (for displaying local filters on that item)
	 * and an additional 'dropAfter' region which is used to allow a user to drop after to augment a slots definition.
	 */
	render() {
		return (
			<React.Fragment>
				{this._renderFlyout()}
				<div
					className="slotEditorItem is-old"
					aria-label={this.props.itemName}
					onKeyPress={ (event) => this.props.a11Y.handleKeyPress(event, this.props.slotDataItemHandler.swapSlotItems.bind(this.props.slotDataItemHandler), /*dropAfter*/false) }
					onKeyDown={ (event) => this.props.a11Y.handleKeyDown(event, { slotDataItemHandler : this.props.slotDataItemHandler, dropSlotItemHandler: this.props.dropSlotItemHandler })}
					onKeyUp={ (event) => this.props.a11Y.handleKeyUp(event, { slotDataItemHandler : this.props.slotDataItemHandler, dropSlotItemHandler: this.props.dropSlotItemHandler })}
					tabIndex="0"
					ref={(node) => { this.domNode = node; }}
				>
					{(this.props.itemName) &&
						<div className="slotEditorItem__nameLine">
							<div className="slotEditorItem__nameLine__itemName"
								onMouseEnter={this._showFlyout}
								onMouseLeave={this._hideFlyout}
							>
								{this._renderItemField()}
							</div>
							{(this.props.slotDataItemHandler.getGraphicData()) &&
								<div className="slotEditorItem__nameLine__icon">
									<span dangerouslySetInnerHTML={{ __html: this.props.slotDataItemHandler.getGraphicData() }} />
								</div>}
							{(!this.props.slotDataItemHandler.isMeasureGroupItem()) &&
								<div className="slotEditorItem__nameLine__actionMenu"
									tabIndex="0"
									onKeyPress={ this._slotActionMenu.bind(this) }
									onClick={ this._slotActionMenu.bind(this) }>
									<SVGIcon iconId={menu_overflow16.id} />
									<div className="actionmenu"/>
								</div>}
						</div>}
					{ this._renderFilterStringAndNumber() }
				</div>
				<div className="slotEditorItem__dropAfter"
					onKeyPress={ (event) => this.props.a11Y.handleKeyPress(event, this.props.slotDataItemHandler.swapSlotItems.bind(this.props.slotDataItemHandler), /*dropAfter*/true) }
					onKeyUp={ (event) => this.props.a11Y.slotHandleKeyUp(event, this.props.dropSlotItemHandler, this.props.slotDataItemHandler)}
					tabIndex="0"
					ref={(node) => { this.domNodeAfter = node; }}>
					<div className="slotEditorItem__dropAfter__indicator"
					/>
				</div>
			</React.Fragment>
		);
	}
	_renderFilterStringAndNumber() {
		const { filterString, numberOfFilterItems } = this.props.slotDataItemHandler.getFilterStringAndNumber() || {};
		if (filterString) {
			return (
				<div className="slotEditorItem__infoLine">
					<div className="slotEditorItem__infoLine__values">
						{ <TruncatedText value={filterString} /> }
					</div>
					{
						numberOfFilterItems ?
							<div className="slotEditorItem__infoLine__filterNumber">
								<div className="slotEditorItem__infoLine__filterNumber__badge">
									{ numberOfFilterItems }
								</div>
							</div> : null
					}
				</div>
			);
		}
		return null;
	}
	//Drag and Drop Support.
	_initializeDndSupport() {
		return this.props.dndManager.then(
			(dndManager) => {
				if (this.domNode) {
					//Initialize a drop handler for both the item node and the 'after node'.
					//Also initialize the node as a drag source.
					this._dndManager = dndManager;
					this.dropSlotItemHandler = new DndSlotItemHandler(
						this.domNode,
						'dndHover',
						this._handleDropSlotItemOnTop.bind(this),
						this._handleAcceptDropSlotItemOnTop.bind(this),
						dndManager
					);

					//We need to set up a callback function when initializing the drag source so that the drag handler is always up-to-date.
					//(not the one at drag drop initialization time!)
					this.dropSlotItemHandler.initializeDndSource(this._dndManager, this._getSlotDataItemHandler.bind(this), this.props.dashboardApi.getFeature('Icons'));
					this.dropSlotItemAfterHandler = new DndSlotItemHandler(
						this.domNodeAfter,
						'dndHover',
						this._handleDropSlotItemAfter.bind(this),
						this._handleAcceptDropSlotItemAfter.bind(this),
						dndManager
					);
					dndManager.addDropTarget(this.domNode, this.dropSlotItemHandler);
					dndManager.addDropTarget(this.domNodeAfter, this.dropSlotItemAfterHandler);
				}
			}
		);
	}

	_getSlotDataItemHandler() {
		return this.props.slotDataItemHandler;
	}

	_handleDropSlotItemOnTop(dragObject) {
		//If the source of the drag is a slot item, we are reorganizing slot items in some way.
		//If its metadata, we're adding new metadata or replacing.
		if (dragObject.type === 'SLOT_ITEM') {
			this.props.slotDataItemHandler.swapSlotItems(this.dropSlotItemHandler.getData(dragObject));
		} else if (dragObject.widgetType === 'shape') {
			this.props.slotDataItemHandler.applyShapeOnSlot(this.dropSlotItemHandler.getData(dragObject));
		} else {
			this.props.slotDataItemHandler.replaceItemWith(this.dropSlotItemHandler.getData(dragObject));
		}
	}

	_handleDropSlotItemAfter(dragObject) {
		//Handles the 'drop after zone'
		if (dragObject.type === 'SLOT_ITEM') {
			this.props.slotDataItemHandler.swapSlotItems(this.dropSlotItemHandler.getData(dragObject), /*after*/true);
		} else {
			this.props.slotDataItemHandler.insertAfterItem(this.dropSlotItemHandler.getData(dragObject), /*after*/true);
		}
	}

	//Can we drop on top of an existing item?
	_handleAcceptDropSlotItemOnTop(dragObject) {
		return this.props.slotDataItemHandler.accepts(this.dropSlotItemHandler.getData(dragObject));
	}

	//Can we drop after an existing item?
	_handleAcceptDropSlotItemAfter(dragObject) {
		return this.props.slotDataItemHandler.accepts(this.dropSlotItemHandler.getData(dragObject), /*after*/true);
	}

	_slotActionMenu() {
		this.props.slotDataItemHandler.openSlotActionMenu(
			this.domNode,
			{
				attachPoint: '.actionmenu',
				placement: 'bottom auto'	//This string is important. Please see https://github.ibm.com/BusinessAnalytics/vis-slot-editor/pull/43 before changing.
			}
		);
	}

	_renderFlyout() {
		/* we dont't want to show tooltip if the item is measure group*/
		const shouldRenderFlyout = this.state.isFlyoutShown && this.domNode && !this.props.slotDataItemHandler.isMeasureGroupItem();
		if ( shouldRenderFlyout ) {
			return (
				<SlotEditorItemFlyout
					domNode={this.domNode}
					dashboardApi={this.props.dashboardApi}
					columnId={this.props.columnId}
					sourceId={this.sourceId}
					onClose={this._hideFlyout}
				/>
			);
		}
	}

	_showFlyout = () => {
		// don't show flyout when doing DND because the mui three flyout somehow mess up the drag object(see defect CADBLW-5243)
		// don't show flyout if slot menu is open
		if(!this._isDragging() && !this.props.slotDataItemHandler.isSlotActionMenuOpened()) {
			this.setState({
				isFlyoutShown: true
			});
		}
	}

	_hideFlyout = () => {
		this.setState({ isFlyoutShown: false });
	}

	_isDragging = () => {
		return !!(this._dndManager && this._dndManager.dragObject);
	}

	_renderItemField() {
		const truncatedTextStyle = {
			minWidth: 100
		};
		return (
			<TruncatedText location={'middle'} value={this.props.itemName} style={truncatedTextStyle}/>
		);
	}
}

SlotEditorItem.propTypes = {
	itemName: PropTypes.string,
	indexInSlot: PropTypes.number,
	itemValues: PropTypes.string,
	dashboardApi: PropTypes.object,
	columnId: PropTypes.string,
	slotDataItemHandler: PropTypes.object.isRequired,
	dropSlotItemHandler: PropTypes.object.isRequired,
	a11Y: PropTypes.object,
	dndManager: PropTypes.object.isRequired
};

export default SlotEditorItem;



// WEBPACK FOOTER //
// ./src/js/OldSlotEditorComponents/SlotEditorItem.js